import {useMemo} from 'react';

import {
    addToSelection,
    clearSelection,
    removeFromSelection,
} from '../reducers';

import {selectionSelector} from '../selectors';

import {
    useAppDispatch,
    useAppSelector
} from '../../../../hooks/useTypedReactRedux';

// Selection lives in the Redux selection slice (FNP-951). This replaced a
// module-level `let globalSelection` + listener Set, which survived unmount and
// route changes (stale checkboxes on revisit) and was never reset between tests.
//
// The slice stores the selected record keyed by id; consumers here check
// `selection[id] === true`, so the boolean map is derived below. Returning the
// raw slice would make every `=== true` check false and silently select nothing.
export const useCodePipelinesSelection = () => {
    const dispatch = useAppDispatch();

    const selected = useAppSelector(selectionSelector);

    const selection: Record<string, boolean> = useMemo(
        () => Object.keys(selected)
                    .reduce((acc, id) => ({
                        ...acc,
                        [id]: true
                    }), {}),
        [selected]
    );

    const handleRowSelection = (id: string, checked: boolean) => {
        if (checked) {
            dispatch(addToSelection({id}));
        } else {
            dispatch(removeFromSelection(id));
        }
    };

    const handleSelectAllRows = (checked: boolean, items: { id: string }[]) => {
        if (checked) {
            items.forEach(item => dispatch(addToSelection({id: item.id})));
        } else {
            dispatch(clearSelection());
        }
    };

    const handleClearSelection = () => {
        dispatch(clearSelection());
    };

    return {
        selection,
        handleRowSelection,
        handleSelectAllRows,
        handleClearSelection,
    };
};
